import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import i18n from 'i18n-js';
import DashedLine from 'react-native-dashed-line';

import { scale } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';

import { font } from '@styles/fonts';

import Footer from './footer';
import Hatch from './hatch';
import ChartLegends from './ChartLegends';
import { TourTarget } from '@components/tours';
import { WELCOME_TOUR_IDS } from '@components/tours/config/global/welcome/constants';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useManageBarChart } from '@components/machines/components/eventsChart/hooks/useManageBarChart';

const Chart = (props) => {
  const { C, isActive, isDay, selectedIndex, setSelectedIndex, setSelectedItem, loaderContainerStyles, renderLegends = true } = props;

  const {
    isLoading,
    legend,
    legendRules,
    dataBar,
    formatedData,
    rightFooter,
    heightValues,
    barBlockWidth,
    barWidth,
    bottomLegendBlockWidth,
    chartHeight,
    chartWidth,
  } = useManageBarChart(props);

  if (isLoading) {
    return (
      <View testID={TEST_IDS.barChart.loadingContainer} style={[{ marginTop: scale(30) }, loaderContainerStyles]}>
        <SmallLoading />
      </View>
    );
  }

  return (
    <TourTarget tourId={WELCOME_TOUR_IDS.PERFORMANCE_BAR_CHART}>
      <View
        testID={TEST_IDS.barChart.chartContainer}
        style={{
          height: scale(135),
          marginTop: scale(4),
          width: chartWidth,
          borderColor: C('Hairline/Light/Secondary'),
          borderLeftWidth: StyleSheet.hairlineWidth,
          borderTopWidth: StyleSheet.hairlineWidth,
        }}
      >
        <View
          style={{
            height: chartHeight,
            width: chartWidth,
            borderColor: C('Hairline/Light/Secondary'),
            borderBottomWidth: StyleSheet.hairlineWidth,
          }}
        >
          <View
            style={{
              height: StyleSheet.hairlineWidth,
              width: '100%',
              backgroundColor: C('Hairline/Light/Secondary'),
              position: 'absolute',
              top: chartHeight / 2,
            }}
          />
          <View
            testID={TEST_IDS.barChart.barsContainer}
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'flex-end',
              height: '100%',
            }}
          >
            {formatedData.map((item, index) => {
              const calc = (i) => (i > 0 ? 1 : 0);

            const render = [];
            const colors = {
              change: C('Status/Light/Change'),
              active: C('Status/Light/Active'),
              setup: C('Status/Light/Setup'),
            };
            const colorsHatch = {
              change: C('Status/Light/Change'),
              active: C('Status/Light/Active'),
              setup: C('Status/Light/Setup'),
            };
            const hatchKey = [];

              heightValues.forEach((key) => {
                const v = item[key + 'Height'];
                render.push({
                  min: calc(v),
                  height: `${(v * 100) / item.height}%`,
                  color: colors[key],
                  showHatch: false,
                  colorHatch: colorsHatch[key],
                  key,
                });
              });

              const minHeight = Object.values(render).reduce((a, i) => i.min + a, 0);
              const isSelected = selectedIndex === index;
              return (
                <TouchableElement
                  key={`bar-${index}`}
                  hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
                  onPress={() => {
                    if (isSelected) {
                      setSelectedIndex(null);
                      setSelectedItem(null);
                    } else {
                      setSelectedIndex(index);
                      item.height = Math.max(minHeight, item.height);
                      setSelectedItem(item);
                    }
                  }}
                >
                  {({ boxStyle }) => {
                    return (
                      <View
                        style={[
                          boxStyle,
                          {
                            height: '100%',
                            flexDirection: 'row',
                            alignItems: 'flex-end',
                            width: barBlockWidth,
                            position: 'absolute',
                            zIndex: 1000,
                            left: index * bottomLegendBlockWidth,
                            opacity: selectedIndex !== null && !isSelected ? 0.5 : 1,
                          },
                        ]}
                      >
                        <View
                          style={[
                            {
                              width: barBlockWidth,
                              height: `${item.height}%`,
                              minHeight: minHeight,
                              alignItems: 'center',
                            },
                          ]}
                        >
                          <View
                            style={[
                              boxStyle,
                              {
                                width: barWidth,
                                height: '100%',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                borderTopLeftRadius: scale(2),
                                borderTopRightRadius: scale(2),
                                overflow: 'hidden',
                                backgroundColor: C('Background/Light/System'),
                              },
                            ]}
                          >
                            {render.map((item) => (
                              <View
                                key={item.key}
                                style={{
                                  width: barWidth,
                                  height: item.height,
                                  backgroundColor: item.color,
                                  minHeight: item.min,
                                  overflow: 'hidden',
                                }}
                              >
                                {item.showHatch && <Hatch {...{ color: item.colorHatch }} />}
                              </View>
                            ))}
                          </View>
                        </View>
                      </View>
                    );
                  }}
                </TouchableElement>
              );
            })}
            {legend.map((item, index) => (
              <View
                key={`legend-${index}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  width: bottomLegendBlockWidth,
                  left: index * bottomLegendBlockWidth,
                  height: scale(136),
                }}
              >
                {legendRules.includes(item) ? (
                  <>
                    <View
                      style={{
                        position: 'absolute',
                        width: StyleSheet.hairlineWidth,
                        height: '100%',
                        left: -StyleSheet.hairlineWidth / 2,
                        opacity: index,
                      }}
                    >
                      <DashedLine
                        axis="vertical"
                        style={{ height: scale(136) }}
                        dashThickness={StyleSheet.hairlineWidth}
                        dashLength={scale(2)}
                        dashGap={scale(2)}
                        dashColor={C('Label/Light/Quaternary')}
                      />
                    </View>
                    <Text
                      numberOfLines={1}
                      style={[
                        font.Footnote,
                        {
                          color: C('Label/Light/Quaternary'),
                          position: 'absolute',
                          bottom: scale(-3),
                          left: (barBlockWidth - barWidth) / 2,
                          minWidth: scale(20),
                        },
                      ]}
                    >
                      {i18n.t(String(item))}
                    </Text>
                  </>
                ) : null}
                {index === legend.length - 1 && (
                  <View
                    style={{
                      position: 'absolute',
                      width: StyleSheet.hairlineWidth,
                      height: '100%',
                      right: 0,
                    }}
                  >
                    <DashedLine
                      axis="vertical"
                      style={{ height: scale(136) }}
                      dashThickness={StyleSheet.hairlineWidth}
                      dashLength={scale(2)}
                      dashGap={scale(2)}
                      dashColor={C('Label/Light/Quaternary')}
                    />
                  </View>
                )}
              </View>
            ))}
          </View>
          {renderLegends && (
            <ChartLegends
              C={C}
              chartHeight={chartHeight}
              dataBar={dataBar}
              heightValues={heightValues}
              isDay={isDay}
              legendsWidth={scale(40)}
              containerStyle={{ position: 'absolute', top: 0, right: scale(-40), height: '100%' }}
            />
          )}
        </View>
        {!isActive && <Footer {...{ ...rightFooter, C, isDay, selectedIndex, loaded: !!dataBar?.length }} />}
      </View>
    </TourTarget>
  );
};

export default Chart;
